import { useState, useEffect, useRef, useCallback } from 'react';
import Session from '../../../Utils/Sessions';
import notificationSound from '../../../assets/sounds/coin_clatter.mp3';

const useWebSocket = (endpoints = []) => {
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [data, setData] = useState({});
  const connectionsRef = useRef({});
  const retryCountsRef = useRef({});
  const reconnectTimeoutsRef = useRef({});
  const isConnectingRef = useRef({});

  const MAX_RETRIES = 5;
  const RETRY_DELAY = 3000; // 3 seconds

  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio(notificationSound);
      audio.volume = 0.3; // Set volume to 30%
      audio.play().catch(error => {
        console.log('🔇 Could not play notification sound:', error);
      });
    } catch (error) {
      console.log('🔇 Error creating audio object:', error);
    }
  }, []);

  const getAuthToken = useCallback(() => {
    return Session.get('token');
  }, []);

  const getWebSocketUrl = useCallback((endpoint) => {
    const token = getAuthToken();
    if (!token) {
      // console.log('❌ No auth token found for WebSocket connection');
      return null;
    }

    // Use the same base URL as the API but convert HTTP to WebSocket
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9000';
    // Convert HTTP to WS and HTTPS to WSS
    const wsBaseUrl = baseUrl
      .replace(/^https:\/\//, 'wss://')
      .replace(/^http:\/\//, 'ws://')
      .replace(/\/$/, '');
    const url = `${wsBaseUrl}${endpoint}?token=${token}`;
    // console.log(`🔌 WebSocket URL constructed: ${wsBaseUrl}${endpoint}?token=${token.substring(0, 10)}...`);
    return url;
  }, [getAuthToken]);

  const updateConnectionStatus = useCallback((endpoint, status) => {
    setConnectionStatuses(prev => ({
      ...prev,
      [endpoint]: status
    }));
  }, []);

  const connectToEndpoint = useCallback((endpoint) => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current[endpoint]) {
      // console.log(`⚠️ Already connecting to ${endpoint}, skipping...`);
      return;
    }

    // Check retry limit
    const retryCount = retryCountsRef.current[endpoint] || 0;
    if (retryCount >= MAX_RETRIES) {
      // console.log(`❌ Max retries (${MAX_RETRIES}) reached for ${endpoint}`);
      updateConnectionStatus(endpoint, 'failed');
      return;
    }

    // Check if already connected
    const existingConnection = connectionsRef.current[endpoint];
    if (existingConnection && existingConnection.readyState === WebSocket.OPEN) {
      // console.log(`✅ ${endpoint} already connected, skipping...`);
      return;
    }

    const url = getWebSocketUrl(endpoint);
    if (!url) {
      // console.log('❌ No auth token available for WebSocket connection');
      updateConnectionStatus(endpoint, 'no-auth');
      return;
    }

    // Close existing connection if any
    if (connectionsRef.current[endpoint]) {
      connectionsRef.current[endpoint].close();
    }

    // console.log(`🔌 Connecting to WebSocket (attempt ${retryCount + 1}/${MAX_RETRIES}): ${endpoint}`);
    // console.log(`🔑 Using token: ${getAuthToken()?.substring(0, 10)}...`);
    isConnectingRef.current[endpoint] = true;
    updateConnectionStatus(endpoint, 'connecting');

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        // console.log(`✅ WebSocket SUCCESSFULLY connected to ${endpoint}`);
        // console.log(`🎯 Connection established at: ${new Date().toLocaleTimeString()}`);
        updateConnectionStatus(endpoint, 'connected');
        retryCountsRef.current[endpoint] = 0; // Reset retry count on successful connection
        isConnectingRef.current[endpoint] = false;

        // Send a ping to confirm connection
        try {
          ws.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
          // console.log(`📤 Sent ping to ${endpoint}`);
        } catch (error) {
          console.log(`⚠️ Could not send ping to ${endpoint}:`, error);
        }
      };

      ws.onmessage = (event) => {
        try {
          const messageData = JSON.parse(event.data);
          // console.log(`📨 WebSocket message received from ${endpoint} at ${new Date().toLocaleTimeString()}:`, messageData);
          // console.log(`📨 Message type: ${messageData.type || 'unknown'}`);
          // console.log(`📨 Message data keys: ${Object.keys(messageData).join(', ')}`);

          // Play notification sound for new messages (but not for ping responses)
          if (messageData.type !== 'pong') {
            playNotificationSound();
            // console.log('🔔 Notification sound played for WebSocket message');
          }

          setData(prev => ({
            ...prev,
            [endpoint]: messageData
          }));
        } catch (error) {
          console.error(`❌ Error parsing WebSocket message from ${endpoint}:`, error);
          console.error('Raw message data:', event.data);
        }
      };

      ws.onerror = (error) => {
        // console.error(`WebSocket error for ${endpoint}:`, error);
        isConnectingRef.current[endpoint] = false;
      };

      ws.onclose = (event) => {
        // console.log(`WebSocket disconnected from ${endpoint}: ${event.code} ${event.reason}`);
        isConnectingRef.current[endpoint] = false;

        // Don't retry if it was a clean close (1000) or if user logged out
        if (event.code === 1000 || !getAuthToken()) {
          updateConnectionStatus(endpoint, 'disconnected');
          return;
        }

        // Increment retry count
        retryCountsRef.current[endpoint] = (retryCountsRef.current[endpoint] || 0) + 1;

        if (retryCountsRef.current[endpoint] < MAX_RETRIES) {
          // console.log(`🔄 Scheduling reconnection for ${endpoint} in ${RETRY_DELAY}ms (attempt ${retryCountsRef.current[endpoint] + 1}/${MAX_RETRIES})`);
          updateConnectionStatus(endpoint, 'reconnecting');

          reconnectTimeoutsRef.current[endpoint] = setTimeout(() => {
            // console.log(`🔄 Attempting reconnection for ${endpoint}`);
            connectToEndpoint(endpoint);
          }, RETRY_DELAY);
        } else {
          // console.log(`❌ Max retries reached for ${endpoint} - giving up`);
          updateConnectionStatus(endpoint, 'failed');
        }
      };

      connectionsRef.current[endpoint] = ws;

    } catch (error) {
      // console.error(`❌ Error creating WebSocket connection to ${endpoint}:`, error);
      updateConnectionStatus(endpoint, 'error');
      isConnectingRef.current[endpoint] = false;
    }
  }, []); // Remove dependencies to prevent re-creation

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      // console.log('❌ No auth token available for WebSocket connections');
      endpoints.forEach(endpoint => {
        updateConnectionStatus(endpoint, 'no-auth');
      });
      return;
    }

    // console.log('🔌 Initiating WebSocket connections to:', endpoints);
    endpoints.forEach(endpoint => {
      // Skip if already connected or connecting
      const existingConnection = connectionsRef.current[endpoint];
      if (existingConnection &&
          (existingConnection.readyState === WebSocket.CONNECTING ||
           existingConnection.readyState === WebSocket.OPEN)) {
        // console.log(`⚠️ Skipping ${endpoint} - already connected/connecting`);
        return;
      }

      // Reset retry count when manually connecting
      retryCountsRef.current[endpoint] = 0;
      connectToEndpoint(endpoint);
    });
  }, []); // Remove all dependencies to prevent re-creation

  const disconnect = useCallback(() => {
    // console.log('🔌 Disconnecting all WebSocket connections...');

    // Clear all timeouts
    Object.values(reconnectTimeoutsRef.current).forEach(clearTimeout);
    reconnectTimeoutsRef.current = {};

    // Close all connections
    Object.entries(connectionsRef.current).forEach(([endpoint, ws]) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(1000, 'Manual disconnect');
      }
      updateConnectionStatus(endpoint, 'disconnected');
    });

    connectionsRef.current = {};
    isConnectingRef.current = {};
  }, []); // Remove dependencies

  const retry = useCallback((endpoint) => {
    if (endpoint) {
      // Reset retry count for specific endpoint
      // console.log(`🔄 Manual retry requested for ${endpoint}`);
      retryCountsRef.current[endpoint] = 0;
      connectToEndpoint(endpoint);
    } else {
      // Reset retry counts for all endpoints
      // console.log(`🔄 Manual retry requested for all endpoints`);
      retryCountsRef.current = {};
      connect();
    }
  }, []); // Remove dependencies

  // Connect when component mounts and when auth token changes
  useEffect(() => {
    const token = getAuthToken();
    if (token && endpoints.length > 0) {
      // Only connect if no connections exist yet
      const hasExistingConnections = endpoints.some(endpoint =>
        connectionsRef.current[endpoint] &&
        (connectionsRef.current[endpoint].readyState === WebSocket.CONNECTING ||
         connectionsRef.current[endpoint].readyState === WebSocket.OPEN)
      );

      if (!hasExistingConnections) {
        // console.log('🔌 Initial WebSocket connection attempt');
        connect();
      }
    }

    // Cleanup on unmount ONLY
    return () => {
      // console.log('🔌 Component unmounting - cleaning up WebSocket connections');
      disconnect();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Check for auth token changes (Session-based storage)
  // Commented out to prevent unnecessary reconnections on component re-renders
  /*
  useEffect(() => {
    const handleStorageChange = () => {
      const token = getAuthToken();
      if (token) {
        connect();
      } else {
        disconnect();
      }
    };

    // Listen for both storage events and custom session events
    window.addEventListener('storage', handleStorageChange);

    // Custom event for Session utility changes (if implemented)
    const handleSessionChange = (event) => {
      if (event.detail?.key === 'token') {
        handleStorageChange();
      }
    };
    window.addEventListener('sessionChanged', handleSessionChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionChanged', handleSessionChange);
    };
  }, [getAuthToken, connect, disconnect]);
  */

  return {
    connectionStatuses,
    data,
    connect,
    disconnect,
    retry
  };
};

export default useWebSocket;

// Specific hooks for different WebSocket endpoints
export const useDashboardWebSocket = () => {
    return useWebSocket(['/ws/finance/dashboard/']);
};

export const useNotificationsWebSocket = () => {
    return useWebSocket(['/ws/finance/notifications/']);
};

export const useFinanceWebSockets = () => {
    return useWebSocket(['/ws/finance/dashboard/', '/ws/finance/notifications/']);
};
